import { Sparkles } from "lucide-react";

export type ChatModelDefinition = {
  value: string;
  label: string;
  tier: "free" | "paid";
  icon?: string;
};

const ICONS = {
  claude: "https://cdn.heckai.weight-wave.com/claude.png",
  deepseek: "https://files.monica-cdn.im/assets/img/apps_avatar/deepseek-chat.png",
  gemini: "https://cdn.heckai.weight-wave.com/gemini.png",
  gpt: "https://cdn.heckai.weight-wave.com/gpt.png",
  grok: "https://files.monica-cdn.im/assets/img/apps_avatar/groq-beta.png",
};

export const DEEP_MODE_MODEL_ID = "deepseek/deepseek-v4-pro";
export const GENIUS_MODEL_ID = "anthropic/claude-sonnet-4.6";

export const FREE_MODELS: ChatModelDefinition[] = [
  {
    value: "deepseek/deepseek-v4-flash",
    label: "DeepSeek v4 Flash",
    tier: "free",
    icon: ICONS.deepseek,
  },
  {
    value: "deepseek/deepseek-v4-pro",
    label: "DeepSeek v4 Pro",
    tier: "free",
    icon: ICONS.deepseek,
  },
  {
    value: "tencent/hy3-preview",
    label: "Tencent Hy3 Preview",
    tier: "free",
  },
  {
    value: "qwen/qwen3.7-plus",
    label: "Qwen 3.7 Plus",
    tier: "free",
  },
  {
    value: "stepfun/step-3.7-flash",
    label: "Step-3.7-Flash",
    tier: "free",
  },
  {
    value: "google/gemini-3.1-flash-lite",
    label: "Gemini 3.1 Flash Lite",
    tier: "free",
    icon: ICONS.gemini,
  },
  {
    value: "google/gemini-3-flash-preview",
    label: "Gemini 3.0 Flash",
    tier: "free",
    icon: ICONS.gemini,
  },
  {
    value: "openai/gpt-5.4-mini",
    label: "GPT 5.4 mini",
    tier: "free",
    icon: ICONS.gpt,
  },
  {
    value: "minimax/minimax-m3",
    label: "Minimax M3",
    tier: "free",
  },
];

export const PAID_MODELS: ChatModelDefinition[] = [
  {
    value: "anthropic/claude-opus-4.8",
    label: "Claude Opus 4.8",
    tier: "paid",
    icon: ICONS.claude,
  },
  {
    value: "anthropic/claude-opus-4.6",
    label: "Claude Opus 4.6",
    tier: "paid",
    icon: ICONS.claude,
  },
  {
    value: "anthropic/claude-sonnet-4.6",
    label: "Claude Sonnet 4.6",
    tier: "paid",
    icon: ICONS.claude,
  },
  {
    value: "google/gemini-3.1-pro-preview",
    label: "Gemini 3.1 Pro",
    tier: "paid",
    icon: ICONS.gemini,
  },
  {
    value: "google/gemini-3.5-flash",
    label: "Gemini 3.5 Flash",
    tier: "paid",
    icon: ICONS.gemini,
  },
  {
    value: "moonshotai/kimi-k2.6",
    label: "Kimi K2.6",
    tier: "paid",
  },
  {
    value: "z-ai/glm-5.1",
    label: "GLM 5.1",
    tier: "paid",
  },
  {
    value: "openai/gpt-5.4",
    label: "GPT 5.4",
    tier: "paid",
    icon: ICONS.gpt,
  },
  {
    value: "x-ai/grok-4.3",
    label: "Grok 4.3",
    tier: "paid",
    icon: ICONS.grok,
  },
];

export const CHAT_MODELS = [...FREE_MODELS, ...PAID_MODELS];
export const DEFAULT_MODEL_ID = "openai/gpt-5.4-mini";
export const FREE_MODEL_IDS = FREE_MODELS.map((model) => model.value);
export const PAID_MODEL_IDS = PAID_MODELS.map((model) => model.value);
export const FREE_MODEL_NAMES = FREE_MODELS.map((model) => model.label);
export const PAID_MODEL_NAMES = PAID_MODELS.map((model) => model.label);
export const MODEL_ICON_BY_NAME = Object.fromEntries(
  CHAT_MODELS.map((model) => [model.label, model.icon])
);

const PaidModelLabel = ({ label }: { label: string }) => (
  <span
    style={{
      color: "#A020F0",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
    }}
  >
    {label}
    <Sparkles size={16} strokeWidth={2.2} />
  </span>
);

export const MODEL_SELECT_OPTIONS = CHAT_MODELS.map((model) => ({
  value: model.value,
  label:
    model.tier === "paid" ? <PaidModelLabel label={model.label} /> : model.label,
}));

export const isPaidModel = (model?: string | null) =>
  Boolean(model && PAID_MODEL_IDS.includes(model));

export const getRandomModel = (switchDeep = false) =>
  switchDeep ? DEEP_MODE_MODEL_ID : DEFAULT_MODEL_ID;
